import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, History, Loader2, Wrench } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/errors';
import { INCIDENTS_KEY, MONITORS_KEY, monitorKey, monitoringApi } from '@/lib/monitoring-api';
import { serversApi } from '@/lib/servers-api';
import { humanDuration, relativeTime } from '@/modules/monitoring/format';
import type { BucketOutage, UptimeBucket } from '@/types/api';

/**
 * One monitor's history: uptime, a bar per bucket, recent checks, and past outages.
 *
 * ## An empty bucket is drawn as a gap, not as success
 *
 * `percentage === null` means no checks landed in that period — a paused monitor, or a scheduler
 * that was not running. Drawing it as a full green bar would invent uptime for a window nobody
 * observed, which is the same lie the "not enough data" handling exists to prevent.
 *
 * Since 2026-10-21 the API returns every period in the window, empty ones included. Before that it
 * returned only the periods that had checks, so no gap was ever drawn, and a monitor added the day
 * before filled the "Last 7 days" row with a day's blocks, which read as days. The caption says how
 * long a block is for the same reason.
 *
 * ## Red is an outage; amber is a failed check that never became one (2026-10-28)
 *
 * Red used to mean "at least one check failed", so a two-check blip that sent nothing looked exactly
 * like the outage that sent an alert. A block is red now only when the monitor was down in it, and
 * opens that outage's page, where its checks and messages are kept. The strip on the Monitoring page
 * shows the last 40 checks, so a few hours after an outage this is where it is found.
 */
export function MonitorDetailPanel({ monitorId }: { monitorId: string }) {
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: monitorKey(monitorId),
    queryFn: () => monitoringApi.get(monitorId, 7),
  });

  const acknowledge = useMutation({
    mutationFn: (id: string) => monitoringApi.acknowledge(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: monitorKey(monitorId) });
      void queryClient.invalidateQueries({ queryKey: MONITORS_KEY });
      void queryClient.invalidateQueries({ queryKey: INCIDENTS_KEY });
    },
  });

  if (detail.isLoading) {
    return <p className="pt-3 text-sm text-muted-foreground">Loading history…</p>;
  }

  const data = detail.data;

  if (!data) {
    return null;
  }

  const { monitor, buckets, recent_checks: checks, incidents } = data;

  return (
    <div className="space-y-5 border-t pt-4">
      <div className="grid gap-4 sm:grid-cols-4">
        <Figure
          label={`Uptime, ${monitor.window_days} days`}
          value={monitor.uptime === null ? 'Not enough data' : `${monitor.uptime}%`}
          hint={
            monitor.uptime === null
              ? 'A percentage from a handful of checks would not mean anything.'
              : `${monitor.uptime_samples} checks, ${monitor.uptime_failed} failed`
          }
        />
        <Figure
          label="Average response"
          value={monitor.average_latency_ms === null ? 'No data yet' : `${monitor.average_latency_ms} ms`}
          hint="Successful checks only."
        />
        <Figure
          label="Checked every"
          value={
            monitor.interval_seconds < 3600
              ? `${monitor.interval_seconds / 60} min`
              : `${monitor.interval_seconds / 3600} h`
          }
          hint={monitor.expectation.description}
        />
        <Figure
          label="Certificate"
          value={
            monitor.certificate_days_remaining === null
              ? 'Not available'
              : `${monitor.certificate_days_remaining} days`
          }
          hint={
            monitor.certificate_expires_at === null
              ? 'Not an HTTPS monitor, or no handshake yet.'
              : 'Read from what the server actually serves.'
          }
        />
      </div>

      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Last {monitor.window_days} days
        </p>
        <div className="flex h-10 items-end gap-px overflow-hidden rounded">
          {buckets.length === 0 && (
            <p className="text-sm text-muted-foreground">No checks recorded yet.</p>
          )}
          {buckets.map((bucket) => (
            <Bar
              key={bucket.starts_at}
              bucket={bucket}
              seconds={data.bucket_seconds}
              createdAt={monitor.created_at}
            />
          ))}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Each block is {humanDuration(data.bucket_seconds)}. Red: the site was down; click it to see
          what happened. Amber: some checks failed, but it was never down. Grey: no checks.{' '}
          {Date.now() - new Date(monitor.created_at).getTime() < monitor.window_days * 86400000 &&
            `Added ${relativeTime(monitor.created_at)}, so the row starts empty. `}
          History is kept for {monitor.retention_days} days.
        </p>
      </div>

      {/* Keyed on the saved values, so a change made anywhere else resets the form to it. */}
      <AutoFixControl
        key={`${monitor.server_id ?? ''}:${String(monitor.auto_fix)}`}
        monitorId={monitor.id}
        kind={monitor.kind}
        serverId={monitor.server_id}
        autoFix={monitor.auto_fix}
      />

      {incidents.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Outages
          </p>
          <div className="space-y-2">
            {incidents.slice(0, 5).map((incident) => (
              <div
                key={incident.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant={incident.is_open ? 'danger' : 'muted'}>
                      {incident.is_open ? 'Ongoing' : humanDuration(incident.duration_seconds)}
                    </Badge>
                    <span className="text-muted-foreground">
                      {new Date(incident.started_at).toLocaleString()}
                    </span>
                    {incident.acknowledged_at !== null && (
                      <span className="text-xs text-muted-foreground">acknowledged</span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-muted-foreground">{incident.cause}</p>

                  {/*
                    What the platform did about it on its own. Shown in full rather than as a
                    status word: "the platform restarted something on my server" is exactly the
                    sentence a customer is entitled to read afterwards, with what and when.
                  */}
                  {incident.remediation !== null && (
                    <div className="mt-2 space-y-1 rounded-md bg-muted/40 px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <Wrench className="size-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium">
                          {incident.remediation.status_label}
                        </span>
                      </div>
                      {incident.remediation.actions.map((action, index) => (
                        <p
                          key={`${incident.id}-fix-${index}`}
                          className={
                            action.succeeded
                              ? 'text-xs text-muted-foreground'
                              : 'text-xs text-amber-600 dark:text-amber-500'
                          }
                        >
                          {action.detail}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <Link
                    to={`/monitoring/outages/${incident.id}`}
                    className="inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-medium text-primary hover:bg-muted/50"
                  >
                    <History className="size-3.5" aria-hidden />
                    What happened
                  </Link>
                  {incident.is_open && incident.acknowledged_at === null && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => acknowledge.mutate(incident.id)}
                      disabled={acknowledge.isPending}
                      title="Acknowledging records that you have seen this. It does not resolve it. Only a successful check can."
                    >
                      <Check className="mr-1 size-3" />
                      Acknowledge
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {checks.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Recent checks
          </p>
          <div className="max-h-56 space-y-1 overflow-y-auto text-sm">
            {checks.map((check) => (
              <div
                key={check.checked_at}
                className="flex items-baseline justify-between gap-3 border-b py-1 last:border-0"
              >
                <span className="shrink-0 text-muted-foreground">
                  {relativeTime(check.checked_at)}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {check.healthy ? (
                    <span className="text-emerald-600">
                      OK{check.status_code !== null && ` · HTTP ${check.status_code}`}
                    </span>
                  ) : (
                    <span className="text-danger">{check.detail}</span>
                  )}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {check.latency_ms} ms
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function Bar({ bucket, seconds, createdAt }: { bucket: UptimeBucket; seconds: number; createdAt: string }) {
  // An outage first, whatever the checks in the block say: the monitor was down in this period, and
  // the block opens the outage's page.
  if (bucket.outage !== null) {
    const label = `${new Date(bucket.starts_at).toLocaleString()} · ${outageSentence(bucket.outage)} Click to see what happened.`;

    return (
      <Link
        to={`/monitoring/outages/${bucket.outage.incident_id}`}
        className="h-10 flex-1 rounded-sm bg-danger transition-opacity hover:opacity-75"
        title={label}
        aria-label={label}
      />
    );
  }

  // An empty bucket is a gap. Drawing it as success would invent uptime for a period nobody
  // observed — see the component docblock. Every period in the window comes back, so the time before
  // the monitor was added is empty too; it is drawn fainter than a gap, because nothing was missed.
  if (bucket.percentage === null) {
    const ends = new Date(bucket.starts_at).getTime() + seconds * 1000;
    const before = ends <= new Date(createdAt).getTime();

    return (
      <div
        className={`flex-1 rounded-sm bg-muted ${before ? 'h-1' : 'h-3'}`}
        title={`${new Date(bucket.starts_at).toLocaleString()}. ${before ? 'Before this monitor was added' : 'No checks'}`}
      />
    );
  }

  // Failed checks that never became an outage: worth seeing, not an alert.
  if (bucket.percentage < 100) {
    const failed = bucket.total - bucket.successful;

    return (
      <div
        className="h-8 flex-1 rounded-sm bg-amber-500"
        title={`${new Date(bucket.starts_at).toLocaleString()} · ${failed} of ${bucket.total} checks failed. Not an outage, so no alert was sent.`}
      />
    );
  }

  return (
    <div
      className="h-6 flex-1 rounded-sm bg-emerald-500"
      title={`${new Date(bucket.starts_at).toLocaleString()} · ${bucket.percentage}% of ${bucket.total} checks`}
    />
  );
}

/** What a red block says on hover: how long it was down, and whether anybody was told. */
function outageSentence(outage: BucketOutage): string {
  const since = new Date(outage.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const more = outage.count > 1 ? ` And ${outage.count - 1} more in this period.` : '';

  if (outage.duration_seconds === null) {
    return `Down since ${since}, still going on.${more}`;
  }

  // A fix can outlast the wait, in which case the alert went out before it worked.
  const told = outage.fixed && !outage.announced
    ? 'Fixed automatically, so no alert was needed.'
    : `${outage.fixed ? 'Fixed automatically. ' : ''}${outage.announced ? 'Alert sent.' : 'No alert was sent.'}`;

  return `Down for ${humanDuration(outage.duration_seconds)} from ${since}. ${told}${more}`;
}

/**
 * Whether the platform may fix what this monitor finds, and on which server.
 *
 * ## Why it lives here rather than in the create form alone
 *
 * Every monitor made before automatic fixes existed has no server linked, and the create form was
 * the only place one could be chosen. Recreating a monitor to link a server would throw away its
 * uptime history, which is the one thing a customer cannot get back.
 *
 * A server monitor's server is its target, so only the switch is offered there.
 */
function AutoFixControl({
  monitorId,
  kind,
  serverId,
  autoFix,
}: {
  monitorId: string;
  kind: string;
  serverId: string | null;
  autoFix: boolean;
}) {
  const queryClient = useQueryClient();
  const [chosenServer, setChosenServer] = useState(serverId ?? '');
  const [enabled, setEnabled] = useState(autoFix);
  const [error, setError] = useState<string | null>(null);
  const canFix = kind === 'server' || kind === 'http' || kind === 'tcp' || kind === 'ssl';

  const servers = useQuery({
    queryKey: ['servers', 'for-monitoring'],
    queryFn: () => serversApi.list(),
    staleTime: 60_000,
    enabled: canFix,
  });

  const save = useMutation({
    mutationFn: () =>
      monitoringApi.setAutoFix(monitorId, {
        auto_fix: enabled,
        server_id: kind === 'server' ? '' : chosenServer,
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: monitorKey(monitorId) });
      void queryClient.invalidateQueries({ queryKey: MONITORS_KEY });
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  if (!canFix) {
    return null;
  }

  const unchanged = enabled === autoFix && (kind === 'server' || chosenServer === (serverId ?? ''));
  const linked = kind === 'server' || chosenServer !== '';

  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Wrench className="size-3.5" />
        Automatic fixes
      </p>

      {kind !== 'server' && (
        <div className="space-y-1">
          <label htmlFor={`autofix-server-${monitorId}`} className="text-sm">
            Server this runs on
          </label>
          <select
            id={`autofix-server-${monitorId}`}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={chosenServer}
            onChange={(event) => setChosenServer(event.target.value)}
          >
            <option value="">Not linked</option>
            {(servers.data?.data ?? []).map((server) => (
              <option key={server.id} value={server.id}>
                {server.name} · {server.ssh_host}
              </option>
            ))}
          </select>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        Try to fix this automatically
      </label>

      <p className="text-xs text-muted-foreground">
        {!enabled
          ? 'Off: when this goes down you are told straight away and nothing is attempted.'
          : linked
            ? 'On: before you are alerted, the platform restarts stopped services, reloads PHP-FPM after a gateway error, reloads the web server for a certificate problem, and clears package caches and old system logs on a full disk. Nothing is rebooted, none of your data is deleted, no firewall rule is changed and no release is rolled back.'
            : 'Choose the server that serves this address, or nothing can be fixed: an address does not say which machine it runs on.'}
      </p>

      {error && <p className="text-xs text-danger">{error}</p>}

      <div className="flex justify-end">
        <Button size="sm" onClick={() => save.mutate()} disabled={unchanged || save.isPending}>
          {save.isPending && <Loader2 className="mr-1 size-3 animate-spin" />}
          Save
        </Button>
      </div>
    </div>
  );
}
